/**
 * PostSessionMoodSheet — 1-tap mood rating sheet that pops after a
 * completed session. Auto-opens from `handleAutoStop` (or any "I
 * finished a session" event).
 *
 * Design rationale:
 *   - Five options (1..5) rather than 3 or 7. Five is enough resolution
 *     to matter longitudinally, few enough that users don't feel forced
 *     to deliberate. Emoji + short label for instant legibility.
 *   - Anonymous users see nothing — ratings tie to a user account. We
 *     silently no-op rather than nag them to sign in (would poison the
 *     post-session calm vibe).
 *   - Skip is always visible. We'd rather get a clean signal from the
 *     users who rate than a polluted stream of accidental mis-taps.
 *   - Note is optional, 280-char capped, and only shown after a rating
 *     is picked. Keeps the default flow to a single tap.
 *
 * Trigger contract: parent sets `open=true` with a context payload
 * (`{ preset, beatType, durationSec }`), the sheet submits to
 * /api/mood, fires `onSubmitted` with the stored doc, and auto-closes.
 */
import React, { useState, useEffect } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const MOOD_OPTIONS = [
  { rating: 1, emoji: '😕', label: 'Rough' },
  { rating: 2, emoji: '🙂', label: 'Okay' },
  { rating: 3, emoji: '😌', label: 'Steady' },
  { rating: 4, emoji: '✨', label: 'Good' },
  { rating: 5, emoji: '🔆', label: 'Amazing' },
];

export const PostSessionMoodSheet = ({ open, context, onClose, onSubmitted }) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset local state whenever the sheet reopens. Stale ratings across
  // separate sessions would be a nasty data-quality bug.
  useEffect(() => {
    if (open) { setRating(null); setNote(''); setBusy(false); }
  }, [open]);

  if (!open) return null;
  // Anon users — silently skip. The calling component already respects
  // `user` but this double-guard protects against misconfigured callers.
  if (!user) return null;

  const submit = async (r) => {
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        rating: r,
        note: note.trim() || null,
        preset_name: context?.preset ?? null,
        beat_type: context?.beatType ?? null,
        duration_sec: context?.durationSec ?? null,
      };
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mood`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save rating');
      onSubmitted?.(data);
      onClose?.();
    } catch (err) {
      // Don't block close on server errors — failure here shouldn't
      // punish the user for completing a session.
      console.warn('Mood rating save failed:', err);
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  const handleChooseRating = (r) => {
    setRating(r);
    // If no note is being typed, auto-submit to keep the "1-tap" promise.
    if (!note) submit(r);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[90] flex justify-center pointer-events-none"
      data-testid="mood-sheet-root"
    >
      <div
        className="pointer-events-auto w-full max-w-lg mx-4 mb-4 sm:mb-6 rounded-2xl border border-white/10 bg-gradient-to-br from-[#1a1030] via-[#0f0822] to-[#0a0118] shadow-2xl shadow-black/50 backdrop-blur-xl p-5"
        data-testid="mood-sheet"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-cyan-300/80 font-semibold">
              Session complete
            </div>
            <h3 className="text-white text-base font-medium mt-0.5">
              How do you feel?
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="mood-sheet-skip"
            className="text-white/40 hover:text-white/80 transition-colors p-1 -mr-1"
            aria-label="Skip"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Emoji row */}
        <div
          className="mt-4 grid grid-cols-5 gap-2"
          data-testid="mood-sheet-options"
        >
          {MOOD_OPTIONS.map((opt) => {
            const selected = rating === opt.rating;
            return (
              <button
                key={opt.rating}
                type="button"
                onClick={() => handleChooseRating(opt.rating)}
                disabled={busy}
                data-testid={`mood-rating-${opt.rating}`}
                className={
                  'flex flex-col items-center gap-1 py-2.5 rounded-xl transition-all ' +
                  (selected
                    ? 'bg-cyan-500/15 border border-cyan-400/40 scale-105'
                    : 'bg-white/[0.03] border border-white/5 hover:bg-white/[0.06] hover:border-white/10')
                }
              >
                <span className="text-2xl leading-none" aria-hidden="true">
                  {opt.emoji}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-white/60">
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Optional note — only show once a rating is picked, keeps the
            default flow to a single tap. */}
        {rating !== null && (
          <div className="mt-3 space-y-2" data-testid="mood-sheet-note-wrap">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 280))}
              placeholder="What stood out? (optional)"
              rows={2}
              disabled={busy}
              data-testid="mood-sheet-note"
              className="w-full rounded-lg bg-white/[0.03] border border-white/10 focus:border-cyan-400/40 focus:outline-none text-white/80 text-sm px-3 py-2 resize-none"
            />
            <div className="flex items-center justify-between text-[11px] text-white/35">
              <span>{note.length}/280</span>
              <Button
                type="button"
                size="sm"
                onClick={() => submit(rating)}
                disabled={busy}
                data-testid="mood-sheet-submit"
                className="h-7 bg-cyan-500 hover:bg-cyan-400 text-black text-xs gap-1.5"
              >
                {busy ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>
                ) : (
                  <><Send className="w-3 h-3" /> Save</>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostSessionMoodSheet;
